import React from 'react';
import { Loader2 } from 'lucide-react';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';

/**
 * DIRECT WORK (MODES.md §6, M6) — one direct run in the room's main stream: the question, the agent working on
 * it (watch it live in the dock), its answer. Labelled "Direct · {agent}" — direct work isn't tied to a task or
 * the plan. Its own block: it never folds into, or resets, a network run's block.
 */
const STATUS = {
  starting: { label: 'Starting', cls: 'bg-gray-100 text-gray-600' },
  running: { label: 'Working', cls: 'bg-indigo-50 text-indigo-700' },
  done: { label: 'Done', cls: 'bg-emerald-50 text-emerald-700' },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700' },
  stopped: { label: 'Stopped', cls: 'bg-amber-50 text-amber-800' },
};

export default function DirectRunCard({ run, mode = '', onWatch = null, onStop = null, stopping = false, renderOutputs = null }) {
  if (!run) return null;
  const st = STATUS[run.status] || STATUS.starting;
  const open = run.status === 'starting' || run.status === 'running';
  return (
    <div data-testid={`direct-run-${run.runId}`} data-status={run.status}
      className="rounded-lg border border-indigo-100 bg-white px-3 py-2 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="rounded bg-indigo-600 px-1.5 py-0.5 font-medium text-white">Direct · {run.agentName || run.agentId}</span>
        {mode ? <span className="text-gray-400">{mode === 'do' ? 'Do' : 'Ask'}</span> : null}
        <span data-testid={`direct-run-status-${run.runId}`} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${st.cls}`}>
          {open ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
          {st.label}
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5">
          {typeof onWatch === 'function' ? (
            <button type="button" data-testid={`direct-run-watch-${run.runId}`} onClick={() => onWatch(run)}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-indigo-600 hover:bg-indigo-50">
              {open ? 'Watch in dock' : 'Open log'}
            </button>
          ) : null}
          {open && typeof onStop === 'function' ? (
            <button type="button" data-testid={`direct-run-stop-${run.runId}`} disabled={stopping}
              onClick={() => onStop(run)}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              {stopping ? 'Stopping…' : 'Stop'}
            </button>
          ) : null}
        </span>
      </div>
      {run.question ? (
        <div className="mt-1 whitespace-pre-wrap text-[13px] text-gray-700">{run.question}</div>
      ) : null}
      {run.answer && !open ? (
        <div data-testid={`direct-run-answer-${run.runId}`} className="mt-2 border-t border-gray-100 pt-2 text-[13px] text-gray-800">
          <MarkdownRenderer content={run.answer} />
        </div>
      ) : null}
      {/* THE WORK CARD (CONTEXT-DOCS.md §H1): a finished DO run shows what it made + the explicit next steps */}
      {mode === 'do' && run.status === 'done' && typeof renderOutputs === 'function' ? renderOutputs(run) : null}
      {run.status === 'failed' && run.error && !run.answer ? (
        <div className="mt-1 text-[12px] text-rose-600">{run.error}</div>
      ) : null}
    </div>
  );
}
